<script lang="ts" setup>
// PLAT-3131 — the step shown between the Plaid offer and Plaid's own overlay.
//
// Plaid asks for bank credentials, which is a jarring thing to be handed with no warning on a
// screen about wiring money. This says what is about to happen, why, and that the credentials are
// never shared, before any of it. It is an OFFER, not a gate: the request completed as CertifID
// at submit and is certified whether or not the participant goes on (PLAT-3130), so nothing here
// is on the critical path.
//
// Shared by Collect and Confirm — the copy makes no reference to either flow.
import { ref } from 'vue';
import PlaidWhyVerifyModal from '@Components/modals/PlaidWhyVerifyModal.vue';

defineProps<{
    /** True while the Plaid link token is being handed over, to stop a double launch. */
    isLaunching?: boolean;
}>();

const emit = defineEmits<{
    (e: 'continue'): void;
}>();

const isWhyVerifyOpen = ref(false);

/**
 * Both entry points land here — the primary button and the explainer sheet's own button — so
 * reading the explanation never costs an extra step.
 */
const onContinue = () => emit('continue');
</script>

<template>
    <app-form-content-layout class="plaid-priming">
        <h1 class="plaid-priming__headline">We need to verify your bank account information</h1>

        <p class="plaid-priming__description">
            Securely link your checking account with Plaid to help us keep your transaction safe.
        </p>

        <button
            type="button"
            class="plaid-priming__why-link"
            data-id="plaid-priming-why-verify"
            @click="isWhyVerifyOpen = true"
        >
            Why verify through my bank?
        </button>

        <ul class="plaid-priming__facts">
            <li class="plaid-priming__fact">
                <app-icon icon="cd-ic-bank" class="plaid-priming__fact-icon" />
                <span>You'll be directed to log in to your bank.</span>
            </li>
            <li class="plaid-priming__fact">
                <app-icon icon="cd-ic-info-circle" class="plaid-priming__fact-icon" />
                <span>Select the account you entered in the previous step.</span>
            </li>
            <li class="plaid-priming__fact">
                <app-icon icon="cd-ic-money-shield" class="plaid-priming__fact-icon" />
                <span>Your credentials are never shared. Plaid uses bank-level encryption.</span>
            </li>
        </ul>

        <!-- Dismissing this never resolves the offer or exits the task; it closes and the
             participant is back here, unchanged. -->
        <plaid-why-verify-modal
            v-model="isWhyVerifyOpen"
            @continue="onContinue"
        />
    </app-form-content-layout>

    <div class="plaid-priming__buttons">
        <app-button
            class="plaid-priming__button"
            expanded
            size="large"
            :is-loading="isLaunching"
            data-id="plaid-priming-continue"
            @onClick="onContinue"
        >
            Continue to Plaid
        </app-button>
    </div>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.plaid-priming {
    text-align: left;
}

.plaid-priming__headline {
    margin-bottom: 1rem;
}

.plaid-priming__description {
    margin-bottom: 1rem;
}

// A button rather than an anchor: it opens a sheet in place, it does not navigate. Styled as a
// link because that is what the design shows and what the affordance reads as.
// align-self, not text-align: the layout centres its inline children, and the design has this
// sitting hard against the left edge under the description.
.plaid-priming__why-link {
    display: inline-block;
    align-self: flex-start;
    margin-bottom: 1.5rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--brand-color, #156fbe);
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
}

.plaid-priming__facts {
    margin: 0 0 1.5rem;
    padding: 1rem;
    border-radius: 0.5rem;
    background: #f4f5f7;
    list-style: none;
}

.plaid-priming__fact {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;

    & + & {
        margin-top: 0.75rem;
    }
}

.plaid-priming__fact-icon {
    flex: 0 0 auto;
    margin-top: 0.125rem;
}
</style>
